/**
 * Comparer les soumissions reçues pour une demande de prix (V1.6-07).
 *
 * Cet écran **n'achète rien.** Il n'a aucun bouton qui engage : pas de
 * « commander », pas de « créer le bon », pas d'envoi fournisseur. Il montre ce
 * que les fournisseurs ont chiffré, quatre façons de couvrir le besoin, et ce
 * que chacune ignore.
 *
 * Depuis V1.6-08 il a un geste, et un seul : **figer un scénario en brouillon
 * de plan d'achat.** Ce n'est pas un achat — c'est écrire quelle option on
 * envisage, avec l'empreinte des données du moment, pour qu'un humain la
 * révise puis l'approuve ailleurs. Le brouillon n'engage rien, et l'approbation
 * elle-même ne commande toujours rien.
 *
 * Trois choses que cet écran refuse de faire, et qui sont la raison d'être de
 * la moitié de son code :
 *
 * * **afficher un zéro à la place d'un inconnu.** Un total qu'on ne peut pas
 *   calculer s'affiche « inconnu », suivi du nom du fournisseur qui n'a pas
 *   chiffré son transport. Un acheteur qui lit « 0,00 $ » conclut gratuit ;
 * * **cacher une offre écartée.** Une soumission illisible, un substitut non
 *   approuvé, une devise absente : tout reste visible, avec l'action à mener.
 *   Ne pas voir la réponse d'un fournisseur fait croire qu'il n'a pas répondu ;
 * * **dire « meilleur ».** Le mot n'est nulle part. Le moins cher, oui ; le
 *   plus rapide, oui. Meilleur pour ce chantier-là, personne ici ne le sait.
 *
 * La mise en page suit la règle du produit : un tableau au bureau, des cartes
 * sur le téléphone. Un magasinier sur un chantier lit un écran de six pouces
 * avec des gants, et un tableau de sept colonnes n'y est pas lisible.
 */
import { Link, useNavigate, useParams } from "react-router-dom";

import { useRfqComparison } from "@/api/comparison";
import { useCreatePurchasePlan, useRfqPurchasePlans } from "@/api/purchase-plans";
import type {
  ComparisonLine,
  ComparisonOffer,
  ProcurementScenario,
  RfqComparison,
} from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import {
  coverageTone,
  formatAmount,
  formatAssertedAvailability,
  formatComparisonWarning,
  formatCoverage,
  formatLeadTime,
  formatScenarioKind,
  formatScenarioStatus,
  formatTotal,
  scenarioStatusTone,
  warningTone,
} from "@/lib/comparison-display";
import { formatDate, formatQuantityWithUnit, pluralize } from "@/lib/format";
import { formatPlanCode, formatPlanStatus, planStatusTone } from "@/lib/plan-display";
import { reportMutationError } from "@/lib/mutation-errors";
import { formatQuoteStatus } from "@/lib/quote-display";

export function RfqComparisonPage() {
  const { rfqId } = useParams<{ rfqId: string }>();
  const query = useRfqComparison(rfqId);

  if (query.isLoading) return <LoadingState label="Calcul de la comparaison…" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (!query.data) return null;

  return <Comparison data={query.data} />;
}

function Comparison({ data }: { data: RfqComparison }) {
  const hasOffers = data.lines.some((line) => line.offers.length > 0);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-ink">Comparaison — {data.rfq_code}</h1>
          <p className="text-[12px] text-ink-subtle">
            Calculée le {formatDate(data.basis.computed_at)} sur{" "}
            {pluralize(data.basis.quote_ids.length, "soumission validée", "soumissions validées")}.
          </p>
        </div>
        <Link
          to={`/rfqs/${data.rfq_id}`}
          className="text-[13px] text-info underline underline-offset-2"
        >
          Retour à la demande
        </Link>
      </header>

      <Recommendation data={data} />
      {data.excluded_quotes.length > 0 && <Excluded data={data} />}

      {!hasOffers ? (
        <EmptyState
          title="Aucune soumission validée"
          description="Une soumission compte à partir du moment où quelqu'un l'a regardée et validée. Tant que personne ne l'a fait, il n'y a rien à comparer."
        />
      ) : (
        <>
          <section className="grid gap-3 sm:grid-cols-2">
            {data.scenarios.map((scenario) => (
              <ScenarioCard
                key={scenario.kind}
                scenario={scenario}
                rfqId={data.rfq_id}
                recommended={scenario.kind === data.recommendation.kind}
              />
            ))}
          </section>

          <ExistingPlans rfqId={data.rfq_id} />

          <Card>
            <CardHeader
              title="Ligne par ligne"
              subtitle="Toutes les réponses reçues, y compris celles qui ne peuvent pas concourir."
            />
            <CardBody className="space-y-5 p-0 sm:p-4">
              {data.lines.map((line) => (
                <LineBlock key={line.line_id} line={line} />
              ))}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}

/**
 * Ce que le calcul peut conclure — ou ne peut pas, et c'est un résultat.
 *
 * L'encadré affiche la raison dans les deux cas. « Je ne peux pas répondre
 * parce que deux fournisseurs n'ont pas chiffré leur transport » dit quoi aller
 * chercher ; un classement calculé sur des zéros inventés ne dit rien.
 */
function Recommendation({ data }: { data: RfqComparison }) {
  const { recommendation } = data;
  const concluded = recommendation.kind !== null;

  return (
    <Card>
      <CardHeader
        title={
          concluded
            ? `Coût connu le plus bas : ${formatScenarioKind(recommendation.kind!)}`
            : "Pas de conclusion possible"
        }
        action={
          <StatusBadge tone={concluded ? "available" : "warning"}>
            {concluded ? "Calculé" : "Incomplet"}
          </StatusBadge>
        }
      />
      <CardBody className="space-y-2">
        <p className="text-[13px] text-ink">{recommendation.reason}</p>
        {recommendation.rejected.length > 0 && (
          <ul className="space-y-1 text-[12px] text-ink-subtle">
            {recommendation.rejected.map((item) => (
              <li key={item.kind}>
                <span className="font-medium">{formatScenarioKind(item.kind)}</span> — {item.reason}
              </li>
            ))}
          </ul>
        )}
        <p className="text-[12px] text-ink-subtle">
          Le choix du fournisseur reste une décision humaine. Cet écran ne commande rien.
        </p>
      </CardBody>
    </Card>
  );
}

/** Les soumissions reçues qui ne comptent pas encore. Montrées exprès. */
function Excluded({ data }: { data: RfqComparison }) {
  return (
    <Card>
      <CardHeader
        title={pluralize(
          data.excluded_quotes.length,
          "soumission hors calcul",
          "soumissions hors calcul",
        )}
        subtitle="Elles existent, mais personne ne les a encore validées."
      />
      <CardBody>
        <ul className="space-y-1 text-[13px]">
          {data.excluded_quotes.map((quote) => (
            <li key={quote.quote_id} className="flex items-center justify-between gap-2">
              <Link
                to={`/quotes/${quote.quote_id}`}
                className="truncate text-info underline underline-offset-2"
              >
                {quote.supplier_name} — {quote.code}
              </Link>
              <StatusBadge tone="unknown">{formatQuoteStatus(quote.status)}</StatusBadge>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

/**
 * Les plans déjà figés pour cette demande.
 *
 * Ils sont montrés ici parce qu'un acheteur qui revient sur la comparaison a
 * besoin de savoir qu'une décision existe déjà, avant d'en figer une seconde
 * qui la contredirait sans que personne ne s'en aperçoive.
 */
function ExistingPlans({ rfqId }: { rfqId: string }) {
  const { data } = useRfqPurchasePlans(rfqId);
  if (!data || data.items.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title={pluralize(data.total, "plan d'achat figé", "plans d'achat figés")}
        subtitle="Une décision déjà écrite pour cette demande. Aucune n'a rien commandé."
      />
      <CardBody className="p-0 sm:p-4">
        <ul className="divide-y divide-border/60">
          {data.items.map((plan) => (
            <li
              key={plan.id}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 sm:px-0"
            >
              <Link
                to={`/purchase-plans/${plan.id}`}
                className="text-[13px] text-info underline underline-offset-2"
              >
                {formatPlanCode(plan)}
              </Link>
              <StatusBadge tone={planStatusTone(plan.status)}>
                {formatPlanStatus(plan.status)}
              </StatusBadge>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}

/**
 * Figer ce scénario en brouillon de plan.
 *
 * Le corps envoyé ne contient **que le nom du scénario** : aucun prix, aucun
 * total, aucun transport. Le serveur recalcule la comparaison et fige *son*
 * résultat, ce qui rend sans effet un chiffre modifié dans le navigateur.
 *
 * Le bouton n'apparaît pas quand le scénario ne conclut pas : figer « aucune
 * combinaison entièrement chiffrée » produirait un plan vide portant un nom
 * rassurant.
 */
function FreezeScenario({ rfqId, scenario }: { rfqId: string; scenario: ProcurementScenario }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const create = useCreatePurchasePlan();

  const usable = scenario.status === "EXACT" || scenario.status === "HEURISTIC";
  if (!usable || !can("ADMIN", "MANAGER", "TECHNICIAN")) return null;

  async function freeze() {
    try {
      const detail = await create.mutateAsync({ rfqId, scenario: scenario.kind });
      toast.success(
        `Plan ${formatPlanCode(detail.plan)} créé`,
        "Brouillon figé. Rien n'est commandé tant que personne ne l'approuve — et l'approbation ne commande rien non plus.",
      );
      navigate(`/purchase-plans/${detail.plan.id}`);
    } catch (error) {
      reportMutationError(toast, "Plan non créé", error);
    }
  }

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => void freeze()}
      disabled={create.isPending}
      data-testid={`freeze-${scenario.kind}`}
    >
      {create.isPending ? "Création…" : "Figer en plan d'achat"}
    </Button>
  );
}

function ScenarioCard({
  scenario,
  rfqId,
  recommended,
}: {
  scenario: ProcurementScenario;
  rfqId: string;
  recommended: boolean;
}) {
  const currencies = Object.keys(scenario.merchandise_by_currency);

  return (
    <Card className={recommended ? "border-status-available/50" : undefined}>
      <CardHeader
        title={formatScenarioKind(scenario.kind)}
        action={
          <span className="flex flex-wrap items-center justify-end gap-1">
            <StatusBadge tone={scenarioStatusTone(scenario.status)}>
              {formatScenarioStatus(scenario.status)}
            </StatusBadge>
            <StatusBadge tone={coverageTone(scenario.coverage)}>
              {formatCoverage(scenario.coverage)}
            </StatusBadge>
          </span>
        }
      />
      <CardBody className="space-y-2">
        <p
          className="text-[18px] font-semibold text-ink"
          data-testid={`total-${scenario.kind}`}
        >
          {formatTotal(scenario.total_cost, scenario.currency, scenario.status)}
        </p>

        {currencies.length > 1 && (
          <ul className="text-[12px] text-ink-subtle">
            {currencies.map((currency) => (
              <li key={currency}>
                Marchandise :{" "}
                {formatAmount(scenario.merchandise_by_currency[currency], currency)}
              </li>
            ))}
          </ul>
        )}

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[12px]">
          <dt className="text-ink-subtle">Fournisseurs</dt>
          <dd className="text-ink">
            {scenario.supplier_count === 0 ? "—" : scenario.supplier_names.join(", ")}
            {scenario.quote_count > scenario.supplier_count && (
              <span className="text-ink-subtle">
                {" "}
                ({pluralize(scenario.quote_count, "soumission", "soumissions")})
              </span>
            )}
          </dd>
          <dt className="text-ink-subtle">Délai</dt>
          <dd className="text-ink">{formatLeadTime(scenario.lead_time_days)}</dd>
        </dl>

        <p className="text-[12px] text-ink-subtle">{scenario.rationale}</p>

        <FreezeScenario rfqId={rfqId} scenario={scenario} />
      </CardBody>
    </Card>
  );
}

function LineBlock({ line }: { line: ComparisonLine }) {
  return (
    <section className="border-t border-border px-4 py-3 first:border-t-0 sm:px-0 sm:pt-0">
      <header className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[14px] font-medium text-ink">
          {line.description}{" "}
          <span className="text-ink-subtle">
            — {formatQuantityWithUnit(line.requested_quantity, line.unit)}
          </span>
        </h3>
        <StatusBadge tone={coverageTone(line.coverage)}>
          {formatCoverage(line.coverage)}
        </StatusBadge>
      </header>

      {line.warnings.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1">
          {line.warnings.map((warning) => (
            <li key={warning}>
              <StatusBadge tone="warning">{formatComparisonWarning(warning)}</StatusBadge>
            </li>
          ))}
        </ul>
      )}

      {line.offers.length === 0 ? (
        <p className="text-[13px] text-ink-subtle">
          Aucune soumission validée ne répond à cette ligne.
        </p>
      ) : (
        <>
          {/* Le tableau au bureau. */}
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[12px] text-ink-subtle">
                  <th className="py-1 pr-3 font-medium">Fournisseur</th>
                  <th className="py-1 pr-3 font-medium">Prix unitaire</th>
                  <th className="py-1 pr-3 font-medium">À commander</th>
                  <th className="py-1 pr-3 font-medium">Marchandise</th>
                  <th className="py-1 pr-3 font-medium">Disponibilité</th>
                  <th className="py-1 pr-3 font-medium">Délai</th>
                </tr>
              </thead>
              <tbody>
                {line.offers.map((offer) => (
                  <OfferRow
                    key={offer.quote_line_id}
                    offer={offer}
                    cheapest={offer.quote_line_id === line.cheapest_quote_line_id}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Les cartes sur le téléphone. */}
          <ul className="space-y-2 sm:hidden">
            {line.offers.map((offer) => (
              <li key={offer.quote_line_id}>
                <OfferCard
                  offer={offer}
                  cheapest={offer.quote_line_id === line.cheapest_quote_line_id}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function OfferRow({ offer, cheapest }: { offer: ComparisonOffer; cheapest: boolean }) {
  return (
    <tr className={offer.eligible ? undefined : "text-ink-subtle"}>
      <td className="py-1.5 pr-3">
        <span className="font-medium text-ink">{offer.supplier_name}</span>
        {cheapest && (
          <StatusBadge tone="available" className="ml-2">
            Moins cher
          </StatusBadge>
        )}
        <OfferWarnings offer={offer} />
      </td>
      <td className="py-1.5 pr-3">{formatAmount(offer.unit_price, offer.currency)}</td>
      <td className="py-1.5 pr-3">
        {formatQuantityWithUnit(offer.purchase_quantity, offer.unit)}
        {offer.package_count !== null && (
          <span className="text-ink-subtle">
            {" "}
            ({pluralize(offer.package_count, "boîte", "boîtes")})
          </span>
        )}
      </td>
      <td className="py-1.5 pr-3">{formatAmount(offer.merchandise_total, offer.currency)}</td>
      <td className="py-1.5 pr-3">
        {formatAssertedAvailability(offer.available_quantity, offer.availability_asserted_on)}
      </td>
      <td className="py-1.5 pr-3">{formatLeadTime(offer.lead_time_days)}</td>
    </tr>
  );
}

function OfferCard({ offer, cheapest }: { offer: ComparisonOffer; cheapest: boolean }) {
  return (
    <Card className={offer.eligible ? undefined : "opacity-80"}>
      <CardBody className="space-y-1 p-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-medium text-ink">{offer.supplier_name}</span>
          {cheapest && <StatusBadge tone="available">Moins cher</StatusBadge>}
        </div>
        <p className="text-[15px] text-ink">
          {formatAmount(offer.merchandise_total, offer.currency)}
          <span className="text-[12px] text-ink-subtle">
            {" "}
            ({formatAmount(offer.unit_price, offer.currency)} l'unité)
          </span>
        </p>
        <p className="text-[12px] text-ink-subtle">
          À commander : {formatQuantityWithUnit(offer.purchase_quantity, offer.unit)}
        </p>
        <p className="text-[12px] text-ink-subtle">
          {formatAssertedAvailability(offer.available_quantity, offer.availability_asserted_on)}
        </p>
        <p className="text-[12px] text-ink-subtle">{formatLeadTime(offer.lead_time_days)}</p>
        <OfferWarnings offer={offer} />
      </CardBody>
    </Card>
  );
}

function OfferWarnings({ offer }: { offer: ComparisonOffer }) {
  if (offer.warnings.length === 0) return null;
  const blocking = new Set(offer.blocking);
  return (
    <ul className="mt-1 flex flex-wrap gap-1">
      {offer.warnings.map((warning) => (
        <li key={warning}>
          <StatusBadge tone={warningTone(warning, blocking.has(warning))}>
            {formatComparisonWarning(warning)}
          </StatusBadge>
        </li>
      ))}
    </ul>
  );
}
